import React, { useState, useEffect } from 'react';
import ServiceToggle from './ServiceToggle';
import { Label } from './ui/label';
import TimeSlotSelector from './TimeSlotSelector';
import { SecondaryButton } from './Buttons';

/**
 * Visual schedule editor with day toggles
 * Used in: AddTeamModal, EditTeamModal
 */
const ScheduleEditor = ({ value = [], onChange }) => {
  const DAYS = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' }
  ];

  // Convert array to day-indexed object for easier manipulation
  const [schedule, setSchedule] = useState(() => {
    const scheduleObj = {};
    DAYS.forEach((day, index) => {
      const existing = value.find(slot => slot.day === day.value);
      scheduleObj[day.value] = existing 
        ? { enabled: true, startTime: existing.startTime, endTime: existing.endTime }
        : { enabled: index === 0, startTime: '09:00', endTime: '17:00' }; // Monday (index 0) enabled by default
    });
    return scheduleObj;
  });

  // Sync schedule object back to array format when it changes
  useEffect(() => {
    const scheduleArray = DAYS
      .filter(day => schedule[day.value]?.enabled)
      .map(day => ({
        day: day.value,
        startTime: schedule[day.value].startTime,
        endTime: schedule[day.value].endTime
      }));
    onChange(scheduleArray);
  }, [schedule]);

  const toggleDay = (dayValue, enabled) => {
    setSchedule(prev => ({
      ...prev,
      [dayValue]: { ...prev[dayValue], enabled }
    }));
  };

  const updateTime = (dayValue, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [dayValue]: { ...prev[dayValue], [field]: value }
    }));
  };

  const toggleAllDaysAllTimes = (enabled) => {
    const newSchedule = {};
    DAYS.forEach(day => {
      newSchedule[day.value] = {
        enabled,
        startTime: '00:00',
        endTime: '23:59'  // True 24/7 coverage
      };
    });
    setSchedule(newSchedule);
  };

  const allDaysEnabled = DAYS.every(day => schedule[day.value]?.enabled);

  return (
    <div className="space-y-4">
      {/* Day Schedule Grid */}
      <div className="space-y-1">
        {DAYS.map((day) => {
          const daySchedule = schedule[day.value] || { enabled: false, startTime: '09:00', endTime: '17:00' };
          
          return (
            <div 
              key={day.value} 
              className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                daySchedule.enabled ? 'bg-white' : 'bg-gray-50'
              }`}
            >
              {/* ServiceToggle — compact, consistent with Services tab */}
              <ServiceToggle
                checked={daySchedule.enabled}
                onChange={(checked) => toggleDay(day.value, checked)}
              />
              
              {/* Day Name */}
              <span className={`w-9 font-medium text-xs flex-shrink-0 ${daySchedule.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                {day.label}
              </span>
              
              {/* Time Selectors */}
              <TimeSlotSelector
                value={daySchedule.startTime}
                onChange={(val) => {
                  if (!daySchedule.enabled) toggleDay(day.value, true);
                  updateTime(day.value, 'startTime', val);
                }}
                disabled={!daySchedule.enabled}
                placeholder="Start"
                className="flex-1 text-xs min-w-0"
              />
              
              <span className={`text-xs flex-shrink-0 ${daySchedule.enabled ? 'text-gray-500' : 'text-gray-300'}`}>to</span>
              
              <TimeSlotSelector
                value={daySchedule.endTime}
                onChange={(val) => {
                  if (!daySchedule.enabled) toggleDay(day.value, true);
                  updateTime(day.value, 'endTime', val);
                }}
                disabled={!daySchedule.enabled}
                placeholder="End"
                className="flex-1 text-xs min-w-0"
              />
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={() => toggleAllDaysAllTimes(!allDaysEnabled)}
          className="flex-1 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {allDaysEnabled ? 'Clear All' : 'Available All Times'}
        </button>
        <button
          type="button"
          onClick={() => {
            const newSchedule = {};
            DAYS.forEach(day => {
              const isWeekday = !['saturday', 'sunday'].includes(day.value);
              newSchedule[day.value] = { enabled: isWeekday, startTime: '09:00', endTime: '17:00' };
            });
            setSchedule(newSchedule);
          }}
          className="flex-1 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Mon-Fri 9–5
        </button>
      </div>
    </div>
  );
};

export default ScheduleEditor;
